// Utilidad para generar PDFs de pedidos y ventas
// Usa jsPDF y jsPDF-autotable para crear documentos PDF

let pdfLibsPromise = null
const loadPdfLibs = async () => {
  if (pdfLibsPromise) return pdfLibsPromise
  pdfLibsPromise = Promise.all([import('jspdf'), import('jspdf-autotable')]).then(([jspdfMod, autoTableMod]) => ({
    jsPDF: jspdfMod.default,
    autoTable: autoTableMod.default
  }))
  return pdfLibsPromise
}

const sanitizeEnvValue = (value) => typeof value === 'string' ? value.trim().replace(/^['"]|['"]$/g, '').trim() : value
const normalizeAssetPath = (value, fallback) => {
  const cleaned = sanitizeEnvValue(value)
  if (!cleaned) return fallback
  if (/^https?:\/\//i.test(cleaned)) return cleaned
  let path = cleaned.replace(/\\/g, '/')
  if (path.startsWith('/public/')) path = path.replace(/^\/public\//, '/')
  if (path.startsWith('public/')) path = path.replace(/^public\//, '')
  if (!path.startsWith('/')) path = `/${path}`
  return path
}
const SYSTEM_NAME = sanitizeEnvValue(import.meta.env.VITE_SYSTEM_NAME) || 'PinoHome'
const SYSTEM_LOGO_PATH = normalizeAssetPath(import.meta.env.VITE_SYSTEM_LOGO_PATH, '/pinohome/logo.png')
let logoDataUrlPromise = null
const getLogoDataUrl = async () => {
  if (logoDataUrlPromise) return logoDataUrlPromise
  logoDataUrlPromise = (async () => {
    const response = await fetch(SYSTEM_LOGO_PATH)
    if (!response.ok) return null
    const blob = await response.blob()
    const reader = new FileReader()
    const base64 = await new Promise((resolve) => {
      reader.onloadend = () => resolve(reader.result || null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
    return typeof base64 === 'string' ? base64 : null
  })().catch(() => null)
  return logoDataUrlPromise
}

/**
 * Función auxiliar para cargar y agregar el logo al PDF
 * @param {jsPDF} doc - Documento PDF
 * @param {number} x - Posición X
 * @param {number} y - Posición Y
 * @param {number} width - Ancho del logo
 * @param {number} height - Alto del logo
 * @returns {Promise<void>}
 */
const addLogoToPDF = async (doc, x, y, width, height) => {
  try {
    const base64 = await getLogoDataUrl()
    if (base64) doc.addImage(base64, 'PNG', x, y, width, height)
  } catch (error) {
    console.warn('Error cargando el logo para el PDF:', error)
    // Si falla, continuar sin logo
    return Promise.resolve()
  }
}

/**
 * Generar PDF de un pedido (para depósito - sin importes, sin colores)
 * @param {Object} order - Objeto del pedido
 * @param {string} filename - Nombre del archivo (opcional)
 */
export const generateOrderPDF = async (order, filename = null) => {
  const { jsPDF, autoTable } = await loadPdfLibs()
  const doc = new jsPDF({ compress: true })
  
  // Configuración
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  let yPosition = margin

  // Agregar logo en el encabezado (40% del tamaño original = 10px)
  const logoHeight = 10
  const logoWidth = logoHeight * 4.5 // Proporción del logo
  const logoX = pageWidth / 2 - logoWidth / 2
  const logoY = yPosition
  
  try {
    await addLogoToPDF(doc, logoX, logoY, logoWidth, logoHeight)
    yPosition += logoHeight + 8
  } catch (error) {
    console.warn('No se pudo cargar el logo, usando texto alternativo')
    // Fallback a texto si no se puede cargar el logo
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text(SYSTEM_NAME, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 8
  }
  
  doc.setFontSize(14)
  doc.setFont('helvetica', 'normal')
  doc.text('ORDEN DE PREPARACION - DEPOSITO', pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 15

  // Línea separadora
  doc.setDrawColor(0, 0, 0)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Información del pedido
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('PEDIDO N°:', margin, yPosition)
  doc.setFontSize(16)
  doc.text(order.orderNumber || order.id, margin + 45, yPosition)
  yPosition += 10
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  const orderDate = order.createdAt?.toDate 
    ? order.createdAt.toDate().toLocaleDateString('es-AR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : new Date().toLocaleDateString('es-AR')
  doc.text(`Fecha: ${orderDate}`, margin, yPosition)
  
  const statusLabels = {
    pending: 'PENDIENTE',
    confirmed: 'CONFIRMADO',
    processing: 'EN PROCESO',
    shipped: 'ENVIADO',
    delivered: 'ENTREGADO',
    cancelled: 'CANCELADO'
  }
  doc.setFont('helvetica', 'bold')
  doc.text(`Estado: ${statusLabels[order.status] || order.status}`, pageWidth - margin, yPosition, { align: 'right' })
  
  yPosition += 15

  // Línea separadora
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Información del cliente
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('INFORMACION DEL CLIENTE', margin, yPosition)
  yPosition += 8
  
  doc.setDrawColor(150, 150, 150)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 8

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  if (order.customer) {
    doc.setFont('helvetica', 'bold')
    doc.text('Nombre Completo:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(order.customer.name || '-', margin + 45, yPosition)
    yPosition += 7
    
    doc.setFont('helvetica', 'bold')
    doc.text('Telefono:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(order.customer.phone || '-', margin + 45, yPosition)
    yPosition += 7
    
    doc.setFont('helvetica', 'bold')
    doc.text('Email:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(order.customer.email || '-', margin + 45, yPosition)
    yPosition += 7
    
    if (order.customer.address) {
      doc.setFont('helvetica', 'bold')
      doc.text('Direccion de Entrega:', margin, yPosition)
      yPosition += 7
      doc.setFont('helvetica', 'normal')
      const addressParts = []
      if (order.customer.address.street) addressParts.push(order.customer.address.street)
      if (order.customer.address.number) addressParts.push(`N° ${order.customer.address.number}`)
      if (order.customer.address.city) addressParts.push(order.customer.address.city)
      if (order.customer.address.province) addressParts.push(order.customer.address.province)
      if (order.customer.address.postalCode) addressParts.push(`(${order.customer.address.postalCode})`)
      
      const addressText = addressParts.join(', ')
      const addressLines = doc.splitTextToSize(addressText, pageWidth - 2 * margin - 45)
      doc.text(addressLines, margin + 5, yPosition)
      yPosition += addressLines.length * 5
    }
  }
  yPosition += 10

  // Línea separadora
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Tabla de productos (sin precios)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('PRODUCTOS A PREPARAR', margin, yPosition)
  yPosition += 8
  
  doc.setDrawColor(150, 150, 150)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 8

  const tableData = (order.items || []).map((item, index) => [
    index + 1,
    item.productName || '-',
    item.sku || '-',
    item.variant || '-',
    item.quantity || 0
  ])

  autoTable(doc, {
    startY: yPosition,
    head: [['#', 'PRODUCTO', 'SKU', 'VARIANTE', 'CANTIDAD']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: [220, 220, 220], 
      textColor: 0, 
      fontStyle: 'bold',
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 10
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 50, halign: 'center' },
      3: { cellWidth: 50, halign: 'center' },
      4: { cellWidth: 30, halign: 'center', fontStyle: 'bold' }
    },
    styles: { 
      fontSize: 10,
      cellPadding: 3
    },
    margin: { left: margin, right: margin },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    }
  })

  yPosition = doc.lastAutoTable.finalY + 20

  // Notas importantes (solo si hay notas)
  if (order.notes) {
    // Calcular altura necesaria para las notas con mejor espaciado
    const notesLines = doc.splitTextToSize(order.notes, pageWidth - 2 * margin - 20)
    const notesHeight = Math.max(35, notesLines.length * 7 + 25)
    
    doc.setDrawColor(200, 200, 200)
    doc.rect(margin, yPosition, pageWidth - 2 * margin, notesHeight, 'S')
    
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text('NOTAS IMPORTANTES:', margin + 8, yPosition + 10)
    yPosition += 15
    
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(0, 0, 0)
    // Agregar cada línea con mejor espaciado
    notesLines.forEach((line, index) => {
      doc.text(line, margin + 8, yPosition + (index * 7))
    })
    yPosition += notesLines.length * 7 + 15
  }

  // Pie de página
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(100, 100, 100)
    doc.text(
      `Pagina ${i} de ${pageCount} - Generado el ${new Date().toLocaleDateString('es-AR')}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    )
  }

  // Guardar PDF
  const finalFilename = filename || `pedido-${order.orderNumber || order.id}.pdf`
  doc.save(finalFilename)
}

/**
 * Generar PDF de una venta (comprobante para cliente)
 * @param {Object} sale - Objeto de la venta
 * @param {string} filename - Nombre del archivo (opcional)
 */
export const generateSalePDF = async (sale, filename = null) => {
  const { jsPDF, autoTable } = await loadPdfLibs()
  const doc = new jsPDF({ compress: true })
  
  // Configuración
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  let yPosition = margin

  // Agregar logo en el encabezado (mismo tamaño que en pedidos: 10px)
  const logoHeight = 10
  const logoWidth = logoHeight * 4.5 // Proporción del logo
  const logoX = pageWidth / 2 - logoWidth / 2
  const logoY = yPosition
  
  try {
    await addLogoToPDF(doc, logoX, logoY, logoWidth, logoHeight)
    yPosition += logoHeight + 8
  } catch (error) {
    console.warn('No se pudo cargar el logo, usando texto alternativo')
    // Fallback a texto si no se puede cargar el logo
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text(SYSTEM_NAME, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 8
  }
  
  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.text('COMPROBANTE DE VENTA', pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 12
  
  // Línea separadora
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Información de la venta en cajas destacadas
  const infoBoxHeight = 28
  doc.setFillColor(245, 245, 245)
  doc.rect(margin, yPosition, pageWidth - 2 * margin, infoBoxHeight, 'F')
  doc.setDrawColor(200, 200, 200)
  doc.rect(margin, yPosition, pageWidth - 2 * margin, infoBoxHeight, 'S')
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 62, 80)
  doc.text('INFORMACION DE LA VENTA', margin + 5, yPosition + 7)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(0, 0, 0)
  
  // Columna izquierda
  doc.setFont('helvetica', 'bold')
  doc.text('Numero de Venta:', margin + 5, yPosition + 14)
  doc.setFont('helvetica', 'normal')
  doc.text(sale.saleNumber || sale.id, margin + 45, yPosition + 14)
  
  doc.setFont('helvetica', 'bold')
  doc.text('Numero de Pedido:', margin + 5, yPosition + 21)
  doc.setFont('helvetica', 'normal')
  doc.text(sale.orderNumber || '-', margin + 45, yPosition + 21)
  
  // Columna derecha
  doc.setFont('helvetica', 'bold')
  doc.text('Fecha:', pageWidth / 2 + 5, yPosition + 14)
  doc.setFont('helvetica', 'normal')
  const saleDate = sale.saleDate?.toDate 
    ? sale.saleDate.toDate().toLocaleDateString('es-AR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : new Date().toLocaleDateString('es-AR')
  const dateLines = doc.splitTextToSize(saleDate, (pageWidth - 2 * margin) / 2 - 10)
  doc.text(dateLines, pageWidth / 2 + 30, yPosition + 14)
  
  const paymentMethods = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia Bancaria',
    tarjeta: 'Tarjeta de Credito/Debito',
    cheque: 'Cheque',
    pendiente: 'Pendiente'
  }
  doc.setFont('helvetica', 'bold')
  doc.text('Metodo de Pago:', pageWidth / 2 + 5, yPosition + 21)
  doc.setFont('helvetica', 'normal')
  const paymentText = paymentMethods[sale.paymentMethod] || sale.paymentMethod || '-'
  const paymentLines = doc.splitTextToSize(paymentText, (pageWidth - 2 * margin) / 2 - 10)
  doc.text(paymentLines, pageWidth / 2 + 30, yPosition + 21)
  
  yPosition += infoBoxHeight + 10

  // Información del cliente
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 62, 80)
  doc.text('DATOS DEL CLIENTE', margin, yPosition)
  yPosition += 7
  
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 7

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(0, 0, 0)
  if (sale.customer) {
    doc.setFont('helvetica', 'bold')
    doc.text('Nombre:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(sale.customer.name || '-', margin + 25, yPosition)
    yPosition += 6
    
    if (sale.customer.phone) {
      doc.setFont('helvetica', 'bold')
      doc.text('Telefono:', margin, yPosition)
      doc.setFont('helvetica', 'normal')
      doc.text(sale.customer.phone, margin + 25, yPosition)
      yPosition += 6
    }
    
    doc.setFont('helvetica', 'bold')
    doc.text('Email:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(sale.customer.email || '-', margin + 25, yPosition)
    yPosition += 6
    
    if (sale.customer.address) {
      doc.setFont('helvetica', 'bold')
      doc.text('Direccion:', margin, yPosition)
      yPosition += 6
      doc.setFont('helvetica', 'normal')
      const addressParts = []
      if (sale.customer.address.street) addressParts.push(sale.customer.address.street)
      if (sale.customer.address.number) addressParts.push(`N° ${sale.customer.address.number}`)
      if (sale.customer.address.city) addressParts.push(sale.customer.address.city)
      if (sale.customer.address.province) addressParts.push(sale.customer.address.province)
      if (sale.customer.address.postalCode) addressParts.push(`(${sale.customer.address.postalCode})`)
      
      const addressText = addressParts.join(', ')
      const addressLines = doc.splitTextToSize(addressText, pageWidth - 2 * margin - 25)
      doc.text(addressLines, margin + 5, yPosition)
      yPosition += addressLines.length * 4
    }

    const fulfillmentMethod = sale?.fulfillment?.method === 'pickup' ? 'pickup' : 'delivery'
    const fulfillmentText = fulfillmentMethod === 'pickup'
      ? 'Retiro por sucursal'
      : 'Envío a domicilio'
    doc.setFont('helvetica', 'bold')
    doc.text('Entrega:', margin, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(fulfillmentText, margin + 25, yPosition)
    yPosition += 6
  }
  yPosition += 6

  // Tabla de productos con precios
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 62, 80)
  doc.text('PRODUCTOS ADQUIRIDOS', margin, yPosition)
  yPosition += 7
  
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 7

  const tableData = (sale.items || []).map((item, index) => [
    index + 1,
    item.productName || '-',
    item.sku || '-',
    item.quantity || 0,
    `$${parseFloat(item.price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    `$${parseFloat((item.price || 0) * (item.quantity || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  ])

  autoTable(doc, {
    startY: yPosition,
    head: [['#', 'PRODUCTO', 'SKU', 'CANT.', 'PRECIO UNIT.', 'SUBTOTAL']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: [44, 62, 80], 
      textColor: 255, 
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 8
    },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 35, halign: 'right' },
      5: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
    },
    styles: { 
      fontSize: 8,
      cellPadding: 2
    },
    margin: { left: margin, right: margin }
  })

  yPosition = doc.lastAutoTable.finalY + 10

  // Resumen financiero destacado
  doc.setFillColor(240, 240, 240)
  const summaryHeight = 40
  doc.rect(margin, yPosition, pageWidth - 2 * margin, summaryHeight, 'F')
  doc.setDrawColor(200, 200, 200)
  doc.rect(margin, yPosition, pageWidth - 2 * margin, summaryHeight, 'S')
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 62, 80)
  doc.text('RESUMEN FINANCIERO', margin + 5, yPosition + 8)
  yPosition += 12

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(0, 0, 0)
  
  const subtotal = sale.subtotal || 0
  const discount = sale.discount || 0
  const taxes = sale.taxes || 0
  const shipping = sale.shipping || 0
  const total = sale.total || 0

  doc.text('Subtotal:', margin + 10, yPosition)
  doc.text(`$${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - margin - 10, yPosition, { align: 'right' })
  yPosition += 5
  
  if (discount > 0) {
    doc.setTextColor(0, 120, 0)
    doc.text('Descuento:', margin + 10, yPosition)
    doc.text(`-$${discount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - margin - 10, yPosition, { align: 'right' })
    doc.setTextColor(0, 0, 0)
    yPosition += 5
  }
  
  if (taxes > 0) {
    doc.text('Impuestos:', margin + 10, yPosition)
    doc.text(`$${taxes.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - margin - 10, yPosition, { align: 'right' })
    yPosition += 5
  }
  
  if (shipping > 0) {
    doc.text('Costo de Envio:', margin + 10, yPosition)
    doc.text(`$${shipping.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - margin - 10, yPosition, { align: 'right' })
    yPosition += 5
  } else if (shipping === 0) {
    doc.text('Costo de Envio:', margin + 10, yPosition)
    doc.setTextColor(0, 120, 0)
    doc.text('GRATIS', pageWidth - margin - 10, yPosition, { align: 'right' })
    doc.setTextColor(0, 0, 0)
    yPosition += 5
  }

  // Línea separadora
  doc.setDrawColor(150, 150, 150)
  doc.line(margin + 10, yPosition, pageWidth - margin - 10, yPosition)
  yPosition += 6

  // Total destacado
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 62, 80)
  doc.text('TOTAL A PAGAR:', margin + 10, yPosition)
  doc.setFontSize(14)
  doc.text(`$${total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - margin - 10, yPosition, { align: 'right' })

  yPosition += 12

  // Notas (solo si hay espacio)
  const pageHeight = doc.internal.pageSize.getHeight()
  if (sale.notes && yPosition < pageHeight - 40) {
    // Calcular altura necesaria para las notas con mejor espaciado
    const notesLines = doc.splitTextToSize(sale.notes, pageWidth - 2 * margin - 20)
    const notesHeight = Math.max(35, notesLines.length * 7 + 25)
    
    doc.setDrawColor(200, 200, 200)
    doc.rect(margin, yPosition, pageWidth - 2 * margin, notesHeight, 'S')
    
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(44, 62, 80)
    doc.text('NOTAS ADICIONALES:', margin + 8, yPosition + 10)
    yPosition += 15
    
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(0, 0, 0)
    // Agregar cada línea con mejor espaciado
    notesLines.forEach((line, index) => {
      doc.text(line, margin + 8, yPosition + (index * 7))
    })
    yPosition += notesLines.length * 7 + 15
  }

  // Pie de página
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Página ${i} de ${pageCount} - ${SYSTEM_NAME} - ${new Date().toLocaleDateString('es-AR')}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    )
  }

  // Guardar PDF
  const finalFilename = filename || `comprobante-venta-${sale.saleNumber || sale.id}.pdf`
  doc.save(finalFilename)
}

export const generateLabelsPDF = async ({ items = [], layout = {}, fields = {}, fileName = 'etiquetas.pdf' } = {}) => {
  const { jsPDF } = await loadPdfLibs()
  const pageSize = layout.pageSize || 'A4'
  const format = pageSize === 'A4' ? 'a4' : pageSize === 'A5' ? 'a5' : pageSize === 'Carta' ? 'letter' : [layout.pageWidth || 210, layout.pageHeight || 297]
  const doc = new jsPDF({ unit: 'mm', format, compress: true })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const labelWidth = Number(layout.labelWidth || 70)
  const labelHeight = Number(layout.labelHeight || 35)
  const margin = Number(layout.margin ?? 5)
  const gapX = Number(layout.gapX ?? 2)
  const gapY = Number(layout.gapY ?? 2)
  const showBorder = layout.showBorder !== false
  const cols = Math.max(1, Math.floor((pageWidth - margin * 2 + gapX) / (labelWidth + gapX)))
  const rows = Math.max(1, Math.floor((pageHeight - margin * 2 + gapY) / (labelHeight + gapY)))
  const itemsPerPage = cols * rows
  const logoDataUrl = fields.logo ? await getLogoDataUrl() : null

  const clampText = (value, max) => {
    const text = String(value || '')
    if (text.length <= max) return text
    return `${text.slice(0, Math.max(0, max - 1))}…`
  }

  const drawLabel = (item, x, y) => {
    if (showBorder) {
      doc.rect(x, y, labelWidth, labelHeight)
    }
    const left = x + 2
    const right = x + labelWidth - 2
    const qrSize = fields.qr && item.qrDataUrl ? Math.min(12, Math.max(8, labelHeight * 0.35)) : 0
    const barcodeHeight = fields.barcode && item.barcodeDataUrl ? Math.min(8, Math.max(6, labelHeight * 0.25)) : 0
    const mediaHeight = Math.max(qrSize, barcodeHeight)
    const mediaTop = y + labelHeight - (mediaHeight || 0) - 2
    const logoHeight = fields.logo && logoDataUrl ? Math.min(6, Math.max(4, labelHeight * 0.2)) : 0
    const logoWidth = logoHeight ? logoHeight * 3.2 : 0
    const textStartX = logoHeight ? left + logoWidth + 2 : left
    let cursor = y + 4
    const canWrite = (lineHeight) => cursor + lineHeight <= mediaTop - 1

    if (logoHeight) {
      doc.addImage(logoDataUrl, 'PNG', left, y + 2, logoWidth, logoHeight)
    }

    doc.setFont('helvetica', 'normal')
    if (fields.name && item.name && canWrite(4)) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(clampText(item.name, logoHeight ? 26 : 32), textStartX, cursor)
      cursor += 4
    }
    if (fields.variant && item.variantLabel && canWrite(3.5)) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
      doc.text(clampText(item.variantLabel, logoHeight ? 28 : 34), textStartX, cursor)
      cursor += 3.5
    }
    if (fields.price && item.priceLabel && canWrite(4)) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(clampText(item.priceLabel, 14), textStartX, cursor)
      cursor += 4
    }
    if (fields.sku && item.sku && canWrite(3.5)) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(6.5)
      doc.text(clampText(`SKU: ${item.sku}`, 30), textStartX, cursor)
      cursor += 3.5
    }
    if (fields.serial && item.serialNumber && canWrite(3.5)) {
      doc.setFontSize(6.5)
      doc.text(clampText(`Serie: ${item.serialNumber}`, 30), textStartX, cursor)
      cursor += 3.5
    }

    if (fields.barcode && item.barcodeDataUrl) {
      const barcodeWidth = qrSize > 0 ? labelWidth - qrSize - 4 : labelWidth - 4
      doc.addImage(item.barcodeDataUrl, 'PNG', left, mediaTop, barcodeWidth, barcodeHeight)
    }
    if (fields.qr && item.qrDataUrl) {
      doc.addImage(item.qrDataUrl, 'PNG', right - qrSize, mediaTop, qrSize, qrSize)
    }
  }

  items.forEach((item, index) => {
    const indexInPage = index % itemsPerPage
    if (index > 0 && indexInPage === 0) {
      doc.addPage()
    }
    const row = Math.floor(indexInPage / cols)
    const col = indexInPage % cols
    const x = margin + col * (labelWidth + gapX)
    const y = margin + row * (labelHeight + gapY)
    drawLabel(item, x, y)
  })

  doc.save(fileName)
}

